import {Box, Stack} from '@dropbox/dig-foundations';
import type {
  RedactedWorkstreamMetric,
  WorkstreamEmployee,
  WorkstreamMetric,
  WorkstreamUpdate,
} from 'client';
import {parseUtcDateInput} from 'helpers/dates';
import {type ReactNode, useContext, useMemo, useState} from 'react';
import styles from 'views/workstreams/detail/WorkstreamDetailsShell.module.css';
import {useWorkstreamJiraElevations} from 'views/workstreams/jiraObjects';
import {getFullWorkstreamMetrics, getWorkstreamDri} from 'views/workstreams/utils';

import {AllQuartersLink} from './AllQuartersLink';
import {FadeIn} from './FadeIn';
import {NorthStarBlock} from './NorthStarBlock';
import {DividedRows, FoldedRows, QuarterSuffix} from './ProgressRow';
import {ScanCoreWorkstreamsContext} from './scanCoreWorkstreams';
import {buildScanProgress, type ProgressItem} from './scanProgressItems';
import {SectionFoldToggle} from './SectionFoldToggle';
import {SectionLabel} from './SectionLabel';
import {useHoverReveal} from './useHoverReveal';

const COLUMN_GAP_PX = 24;
// Below this per-column width the columns stack (the old Metrics | Milestones
// boxes' rule).
const MIN_COLUMN_WIDTH_PX = 400;
// Two columns while each fits, sized to the box.
const COLUMNS_STYLE = {
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, minmax(max(${MIN_COLUMN_WIDTH_PX}px, calc((100% - ${COLUMN_GAP_PX}px) / 2)), 1fr))`,
  gap: COLUMN_GAP_PX,
};

// Rows per group before "More": covers 84% of cards with Jira; the tail is a
// few Jira-heavy ones (up to 38 open, incl. two top-50 cards with 17 and 22).
const GROUP_ROW_CAP = 5;

// The update's readable width (WorkstreamScanList's UPDATE_MAX_WIDTH_PX): the
// north star and a one-column box end where the update text above does, the
// same whether the fold is open or not.
const READABLE_WIDTH_PX = 720;

const DAY_MS = 24 * 60 * 60 * 1000;
// Without an update before the latest, milestones closed this recently count.
const RECENT_CLOSE_FALLBACK_DAYS = 14;

// Milestones closed since the update before the latest stay on the card: the
// latest update is often written right after closing one, so it covers those
// too.
const recentCloseCutoff = (updates?: WorkstreamUpdate[]) => {
  const previous = updates?.[1]?.created_at;
  return previous
    ? parseUtcDateInput(previous).getTime()
    : Date.now() - RECENT_CLOSE_FALLBACK_DAYS * DAY_MS;
};

// One type's rows under its label (with `action` on hover). Items arrive
// attention first, so the cap never hides one ahead of a calm one. Read-only:
// metrics and milestones edit on the detail page.
const GroupRows = ({
  label,
  items,
  action,
}: {
  label: ReactNode;
  items: ProgressItem[];
  action?: ReactNode;
}) => {
  const {regionProps, revealStyle} = useHoverReveal();
  if (items.length === 0) return null;
  return (
    <Box {...regionProps} style={{minWidth: 0}}>
      <SectionLabel action={action && <Box style={revealStyle}>{action}</Box>}>
        {label}
      </SectionLabel>
      <Stack gap="12">
        <DividedRows items={items.slice(0, GROUP_ROW_CAP)} />
        <FoldedRows label="More" items={items.slice(GROUP_ROW_CAP)} />
      </Stack>
    </Box>
  );
};

/**
 * /workstreams: the open card's north star, then everything else
 * behind a "› Metrics & milestones" toggle like Sub-workstreams' below it —
 * folded when there's a north star to read instead, open when there isn't
 * (most cards below the top 50). Open, it's the old split — Metrics |
 * Milestones + Roadmap initiatives — each attention first, capped. Closed items
 * are left to the detail page, but for milestones closed since the previous
 * update, which read as news ("Completed Oct 14").
 */
export const ScanProgressList = ({
  workstream,
  openFold = false,
}: {
  // Start open even with a north star: a card that opened on its own (the only
  // one in view) shows everything, as its Sub-workstreams do.
  openFold?: boolean;
  workstream: {
    id: number;
    name: string;
    updates?: WorkstreamUpdate[];
    employee_associations: WorkstreamEmployee[];
    metrics: Array<WorkstreamMetric | RedactedWorkstreamMetric>;
  };
}) => {
  const metrics = useMemo(() => getFullWorkstreamMetrics(workstream.metrics), [workstream.metrics]);
  // An open card is read closely, so its Jira items refresh in the background.
  const {jiraObjects} = useWorkstreamJiraElevations(workstream, true, {refresh: true});
  const isCore = useContext(ScanCoreWorkstreamsContext)?.has(workstream.id) ?? false;
  const owner = getWorkstreamDri(workstream as Parameters<typeof getWorkstreamDri>[0]);
  const closedSince = useMemo(() => recentCloseCutoff(workstream.updates), [workstream.updates]);
  const progress = useMemo(
    () =>
      buildScanProgress({
        workstreamId: workstream.id,
        metrics,
        jiraObjects,
        isCore,
        fallbackOwner: owner,
        closedSince,
      }),
    [workstream.id, metrics, jiraObjects, isCore, owner, closedSince]
  );
  // Without a north star, these are all the card has to show.
  const [open, setOpen] = useState(() => openFold || !progress.pinned);
  // Opened by a click (not open from the start), so it fades in.
  const [toggled, setToggled] = useState(false);
  const {pinned, missingNorthStar, metrics: openMetrics, milestones, initiatives} = progress;
  const hasFolded = openMetrics.length + milestones.length + initiatives.length > 0;

  if (!pinned && !missingNorthStar && !hasFolded) return null;

  // Each type's group, with roughly how many lines it takes (label, capped
  // rows, More toggle), for balancing the two columns.
  const group = (
    key: string,
    label: ReactNode,
    items: ProgressItem[],
    // Beside the label, revealed on hover.
    action?: ReactNode
  ) =>
    items.length > 0
      ? {
          lines: 1 + Math.min(items.length, GROUP_ROW_CAP) + (items.length > GROUP_ROW_CAP ? 1 : 0),
          node: <GroupRows key={key} label={label} items={items} action={action} />,
        }
      : undefined;
  const metricsGroup = group(
    'metrics',
    <>
      Metrics
      <QuarterSuffix />
    </>,
    openMetrics,
    <AllQuartersLink />
  );
  const milestonesGroup = group('milestones', 'Milestones', milestones);
  const initiativesGroup = group('initiatives', 'Roadmap initiatives', initiatives);
  // One type: one column. Two: side by side. All three (14% of cards):
  // Milestones joins whichever side is shorter — under a short Metrics list, or
  // over Roadmap initiatives when the metrics run long.
  const columns: Array<Array<NonNullable<typeof metricsGroup>>> = (() => {
    const present = [metricsGroup, milestonesGroup, initiativesGroup].filter(
      (entry): entry is NonNullable<typeof entry> => Boolean(entry)
    );
    if (present.length < 3 || !metricsGroup || !milestonesGroup || !initiativesGroup) {
      return present.map((entry) => [entry]);
    }
    return metricsGroup.lines <= initiativesGroup.lines
      ? [[metricsGroup, milestonesGroup], [initiativesGroup]]
      : [[metricsGroup], [milestonesGroup, initiativesGroup]];
  })();
  const columnNodes = columns.map((column, index) => (
    <Stack key={index} gap="24">
      {column.map((entry) => entry.node)}
    </Stack>
  ));

  return (
    <Stack gap="16">
      {(pinned || missingNorthStar) && (
        <Box style={{maxWidth: READABLE_WIDTH_PX}}>
          <NorthStarBlock item={pinned} />
        </Box>
      )}
      {hasFolded && (
        <Box>
          <SectionFoldToggle
            fold="metrics_and_milestones"
            label="Metrics & milestones"
            open={open}
            onToggle={() => {
              setToggled(true);
              setOpen((value) => !value);
            }}
          />
          {open && (
            <FadeIn enabled={toggled}>
              {/* Boxed like Sub-workstreams: both columns across the title column,
                  or one column at the north star's (the update's) width, not a
                  full-width box with half of it empty. */}
              {columnNodes.length > 1 ? (
                <Box className={styles.updateSectionCard}>
                  <Box style={COLUMNS_STYLE}>{columnNodes}</Box>
                </Box>
              ) : (
                <Box className={styles.updateSectionCard} style={{maxWidth: READABLE_WIDTH_PX}}>
                  {columnNodes}
                </Box>
              )}
            </FadeIn>
          )}
        </Box>
      )}
    </Stack>
  );
};
